import { useEffect, useState } from 'react'
import { db, type Transaction } from './db/database'
import { useLiveQuery } from 'dexie-react-hooks'
import { ensureBootstrapped } from './db/bootstrap'
import { needsOnboarding } from './db/onboarding'
import { deleteTransaction, pendingClose, plannedTags } from './db/budget'
import { catchUpNotifications } from './db/notification-rules'
import { currentAccountId } from './db/notifications'
import { today } from './lib/dates'
import { CycleLockContext } from './lib/lock'
import { navigate, useRoute, STANDALONE_ROUTES } from './lib/router'
import { isSignedIn } from './db/auth'
import { startAutoPull } from './db/sync'
import { ensurePersistentStorage } from './lib/storage'
import { AuthGate } from './components/AuthGate'
import { AppHeader } from './components/AppHeader'
import { NotificationsSheet } from './components/NotificationsSheet'
import { DayConfirmSheet } from './components/DayConfirmSheet'
import type { NotificationTarget } from './lib/notification-target'
import { AdminScreen } from './screens/AdminScreen'
import { BottomNav } from './components/BottomNav'
import { AddTransactionSheet } from './components/AddTransactionSheet'
import { ConfirmDeleteSheet } from './components/ConfirmDeleteSheet'
import { TransactionDetailSheet } from './components/TransactionDetailSheet'
import { OnboardingSheet } from './components/OnboardingSheet'
import { CycleCloseSheet } from './components/CycleCloseSheet'
import { CycleLockBanner } from './components/CycleLockBanner'
import { HomeScreen } from './screens/HomeScreen'
import { TransactionsScreen } from './screens/TransactionsScreen'
import { BudgetScreen } from './screens/BudgetScreen'
import { ProfileScreen } from './screens/ProfileScreen'
import './App.css'

export default function App() {
  const route = useRoute()
  const [walletId, setWalletId] = useState<string | null>(null)
  // Une seule feuille pour créer et pour corriger : `editing` porte l'opération
  // ouverte, `adding` la création.
  const [adding, setAdding] = useState(false)
  // Toucher une ligne mène à la LECTURE ; corriger est un geste choisi.
  const [viewing, setViewing] = useState<Transaction | null>(null)
  const [editing, setEditing] = useState<Transaction | null>(null)
  // Une suppression d'argent ne se rattrape pas : elle passe toujours par une
  // confirmation, quel que soit le chemin — menu, glissement ou poubelle.
  const [deleting, setDeleting] = useState<{ transaction: Transaction; label: string } | null>(null)
  // Le centre de notifications s'ouvre depuis l'en-tête, donc depuis n'importe
  // quel écran : son état vit ici et non dans l'accueil.
  const [notifications, setNotifications] = useState(false)
  // La journée qu'une notification a demandé d'ouvrir, pour la confirmer.
  const [confirmingDay, setConfirmingDay] = useState<string | null>(null)

  /*
   * Suivre une notification jusqu'à son objet.
   *
   * Trois cas, et le troisième est le seul à pouvoir échouer : une opération
   * peut avoir été supprimée depuis. On ouvre alors l'onglet sans rien de plus
   * — mieux vaut une liste qu'une feuille vide sur une ligne qui n'existe plus.
   */
  function openTarget(target: NotificationTarget) {
    if (target.kind === 'route') { navigate(target.route); return }

    navigate('/transactions')
    if (target.kind === 'day') { setConfirmingDay(target.day); return }

    void db.transactions.get(target.id).then((transaction) => {
      if (transaction && !transaction.deleted_at) setViewing(transaction)
    })
  }

  /*
   * L'entrée dans l'app — décision 2026-08-24.
   *
   * Le compte est exigé au TOUT PREMIER lancement, et une seule fois : le jeton
   * vit ensuite sur l'appareil, donc rouvrir l'app ne redemande jamais rien et
   * tout fonctionne hors ligne. C'est un état, pas une route : personne ne doit
   * pouvoir contourner le portail en tapant une URL.
   */
  const [signedIn, setSignedIn] = useState(isSignedIn)

  /*
   * Portefeuille et catégories de départ, une fois l'entrée franchie.
   *
   * Conditionné au compte : rien ne doit s'écrire dans Dexie — ni portefeuille,
   * ni file de synchronisation — tant que l'utilisateur n'est pas entré. Le
   * portefeuille reste néanmoins créé LOCALEMENT, avec un identifiant généré
   * côté client : il remonte tel quel à la première synchro, où le serveur
   * l'adopte (voir `Sync::Push#adopt_wallet!`).
   */
  useEffect(() => {
    if (!signedIn) return
    // Le bootstrap D'ABORD : il adopte le portefeuille du compte s'il en
    // existe un, et fait lui-même la première descente dans ce cas. Lancer
    // les deux en parallèle ferait courir un pull sur un portefeuille dont
    // l'identifiant n'est pas encore arrêté.
    void ensureBootstrapped().then((id) => {
      setWalletId(id)
      // Ce qui a été saisi ailleurs depuis la dernière ouverture. Sans ça,
      // rien ne descend tant que personne n'appuie sur « Synchroniser ».
      startAutoPull()
    })
  }, [signedIn])

  /*
   * Demander au navigateur de ne pas effacer nos données.
   *
   * Sans ça, IndexedDB est en « best-effort » et s'évince sous pression de
   * stockage — la norme sur un téléphone d'entrée de gamme. Tant que la
   * synchronisation n'a pas tourné, il n'existe AUCUN filet.
   */
  useEffect(() => { void ensurePersistentStorage() }, [])

  /*
   * La date, surveillée.
   *
   * `useLiveQuery` ne se réveille que sur une écriture en base — jamais sur le
   * passage du temps. Une app laissée ouverte le 31 au soir se retrouverait le
   * 1er sans jamais proposer la clôture, et l'utilisateur saisirait ses
   * dépenses de mars dans un février jamais soldé. Le voyage dans le temps du
   * Profil, lui, recharge la page : il retombe sur le même chemin.
   */
  const [date, setDate] = useState(today)
  useEffect(() => {
    const id = setInterval(() => setDate(today()), 60_000)
    return () => clearInterval(id)
  }, [])

  /*
   * Le rattrapage des notifications.
   *
   * Il n'y a pas de « moment où une notification part » : le Web ne sait pas
   * programmer un réveil (décision 2026-09-05). On recalcule donc ce que l'app
   * a à dire à chaque fois qu'une des choses qui les justifient a bougé.
   *
   * Les signaux sont des COMPTES et non les données elles-mêmes : ils changent
   * quand une journée est confirmée, quand un conflit apparaît, quand une
   * dépense entre — et surtout, ils ne changent PAS quand une notification est
   * écrite. Sans cette asymétrie, l'effet se rappellerait lui-même sans fin.
   */
  const signals = useLiveQuery(async () => {
    if (!walletId) return null
    const [pending, conflicts, operations] = await Promise.all([
      db.budget_allocations.where({ wallet_id: walletId })
        .filter((a) => !a.deleted_at && (a.confirmed_at ?? null) === null).count(),
      db.conflicts.count(),
      db.transactions.where({ wallet_id: walletId }).filter((t) => !t.deleted_at).count(),
    ])
    return `${pending}:${conflicts}:${operations}`
  }, [walletId], null)

  useEffect(() => {
    const accountId = currentAccountId()
    if (!walletId || !accountId || signals === null) return
    // Jamais attendu : c'est du confort d'affichage, et rien de ce qui est à
    // l'écran n'en dépend.
    void catchUpNotifications({ walletId, accountId, date })
  }, [walletId, date, signals])

  // useLiveQuery et non un effet : l'accueil doit disparaître de lui-même dès
  // que l'utilisateur a posé son rythme et son montant.
  const onboarding = useLiveQuery(
    async () => (walletId ? needsOnboarding(walletId) : false), [walletId], false)

  // Ce dont la vue détail a besoin, et qu'une transaction seule ne porte pas.
  const details = useLiveQuery(async () => (walletId ? {
    categories: await db.categories.where({ wallet_id: walletId }).toArray(),
    plans: await plannedTags(walletId),
  } : null), [walletId], null)

  const close = useLiveQuery(
    async () => (walletId ? await pendingClose(walletId, date) : null),
    [walletId, date], null)

  /*
   * La feuille s'ouvre SEULE dès qu'un cycle est à solder — au démarrage comme
   * au franchissement de la frontière. On mémorise le cycle refermé, et non un
   * simple booléen : refermer août ne doit pas escamoter la clôture de
   * septembre le mois suivant.
   */
  const [dismissed, setDismissed] = useState<string | null>(null)
  const locked = close != null
  const closing = locked && dismissed !== close.range.start

  // Le panneau d'administration vit hors de l'app : ni portail d'entrée — un
  // administrateur n'a pas de compte utilisateur — ni navigation basse.
  if (STANDALONE_ROUTES.includes(route)) return <AdminScreen />

  if (!signedIn) return <AuthGate onDone={() => setSignedIn(true)} />

  if (!walletId) return <main className="app" />

  return (
    // Le verrou vaut pour toute l'app, feuille ouverte ou refermée : c'est la
    // clôture EN ATTENTE qui interdit d'écrire, pas la feuille à l'écran.
    <CycleLockContext.Provider value={locked}>
      <div className="app">
        {/* L'en-tête, hors du conteneur qui défile : c'est une ligne de la
            grille `.app`, au même titre que la navigation basse. Il vit ICI et
            non dans chaque écran — une barre par écran finirait par diverger,
            et le retour n'aurait plus le même sens d'un onglet à l'autre. */}
        <AppHeader route={route} onNotifications={() => setNotifications(true)} />

        <main className="app-body">
          {locked && (
            <CycleLockBanner close={close} onOpen={() => setDismissed(null)} />
          )}

          {route === '/' && (
            <HomeScreen
              walletId={walletId} onOpen={setViewing} onEdit={setEditing}
              onDelete={(transaction, label) => setDeleting({ transaction, label })}
            />
          )}
          {route === '/transactions' && (
            <TransactionsScreen
              walletId={walletId} onOpen={setViewing} onEdit={setEditing}
              onDelete={(transaction, label) => setDeleting({ transaction, label })}
            />
          )}
          {route === '/budget' && <BudgetScreen walletId={walletId} onOpen={setViewing} />}
          {route === '/profil' && (
            <ProfileScreen walletId={walletId}
                           onSignedOut={() => { setSignedIn(false); setWalletId(null) }} />
          )}
        </main>

        <BottomNav current={route} onAdd={() => setAdding(true)} />

        {adding && (
          <AddTransactionSheet walletId={walletId} onClose={() => setAdding(false)} />
        )}

        {viewing && (
          <TransactionDetailSheet
            transaction={viewing}
            category={details?.categories.find((c) => c.id === viewing.category_id)}
            plan={details?.plans.get(viewing.id)}
            onClose={() => setViewing(null)}
            onEdit={(transaction) => { setViewing(null); setEditing(transaction) }}
            onDelete={(transaction, label) => {
              setViewing(null)
              setDeleting({ transaction, label })
            }}
          />
        )}

        {editing && (
          <AddTransactionSheet
            walletId={walletId}
            editing={editing}
            onClose={() => setEditing(null)}
            onDelete={(transaction, label) => {
              setEditing(null)
              setDeleting({ transaction, label })
            }}
          />
        )}

        {deleting && (
          <ConfirmDeleteSheet
            transaction={deleting.transaction}
            label={deleting.label}
            onCancel={() => setDeleting(null)}
            onConfirm={() => {
              const cible = deleting.transaction
              setDeleting(null)
              void deleteTransaction(walletId, cible.id)
            }}
          />
        )}

        {closing && (
          <CycleCloseSheet
            walletId={walletId}
            close={close}
            // La feuille disparaît d'elle-même : `pendingClose` retombe à null
            // dès que la clôture est écrite, et le verrou se lève avec elle.
            onDone={() => setDismissed(null)}
            onDismiss={() => setDismissed(close.range.start)}
          />
        )}

        {notifications && (
          <NotificationsSheet onClose={() => setNotifications(false)}
                              onOpenTarget={openTarget} />
        )}

        {confirmingDay && (
          <DayConfirmSheet walletId={walletId} day={confirmingDay}
                           onClose={() => setConfirmingDay(null)} />
        )}

        {onboarding && <OnboardingSheet walletId={walletId} />}
      </div>
    </CycleLockContext.Provider>
  )
}
